/**
 * Browser audio capture.
 *
 * This module exists to make a specific class of competitor bug impossible. Verbi's
 * Android build crashes on microphone press and its audio dies after three lessons;
 * both are lifecycle bugs. So here:
 *
 *   - a **pre-flight check** runs before any capture and reports the *specific* cause
 *     of a failure, never a generic error and never a silent dead mic
 *   - the audio graph has one explicit owner and is torn down deterministically
 *   - a watchdog detects a dead input stream and rebuilds once, then reports so the
 *     caller can degrade to text rather than leaving the learner stuck
 *
 * See docs/02-user-flows.md §9 and docs/10-quality-privacy.md §2.
 */

export type PreflightProblem =
  | 'unsupported'
  | 'permission_denied'
  | 'permission_prompt_required'
  | 'no_device'
  | 'device_busy'
  | 'insecure_context';

export interface PreflightResult {
  readonly ok: boolean;
  readonly problems: readonly PreflightProblem[];
  /** Learner-facing explanation. Specific, and never blaming the learner. */
  readonly message: string | null;
}

const MESSAGES: Record<PreflightProblem, string> = {
  unsupported: "This browser can't record audio. You can still use text mode.",
  permission_denied:
    'Microphone access is blocked. You can turn it back on in your browser settings — text mode works meanwhile.',
  permission_prompt_required: 'We need microphone access to score your speaking.',
  no_device: "We couldn't find a microphone. Plug one in, or use text mode.",
  device_busy: 'Your microphone is in use by another app.',
  insecure_context: 'Recording needs a secure (https) connection.',
};

/**
 * Checks everything that can prevent capture, *before* claiming the microphone. Runs in
 * well under the 400 ms budget because it performs no I/O beyond a permission query.
 */
export async function preflight(): Promise<PreflightResult> {
  const problems: PreflightProblem[] = [];

  if (typeof navigator === 'undefined' || navigator.mediaDevices?.getUserMedia === undefined) {
    problems.push('unsupported');
  } else if (globalThis.isSecureContext === false) {
    problems.push('insecure_context');
  } else {
    // Permissions API is not universal; absence is not a failure.
    try {
      const status = await navigator.permissions?.query({
        name: 'microphone' as PermissionName,
      });
      if (status?.state === 'denied') problems.push('permission_denied');
      else if (status?.state === 'prompt') problems.push('permission_prompt_required');
    } catch {
      // Treat an unavailable Permissions API as "we'll find out when we ask".
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter((device) => device.kind === 'audioinput');
      if (inputs.length === 0) problems.push('no_device');
    } catch {
      // Enumeration can fail before permission is granted; not fatal.
    }
  }

  // A pending permission prompt is not a blocker — it is the next step.
  const blocking = problems.filter((p) => p !== 'permission_prompt_required');
  const first = problems[0];

  return {
    ok: blocking.length === 0,
    problems,
    message: first === undefined ? null : MESSAGES[first],
  };
}

export interface RecorderEvents {
  /** 0–1 input level, for the waveform. */
  readonly onLevel?: (level: number) => void;
  /** Client-side VAD: fired once speech is detected. */
  readonly onSpeechStart?: () => void;
  /** Fired after sustained silence following speech. */
  readonly onSpeechEnd?: () => void;
  /**
   * The input stream died and could not be rebuilt. The caller degrades the session to
   * text — it must never leave the learner holding a dead microphone.
   */
  readonly onDead?: (reason: 'input_stalled' | 'track_ended') => void;
}

export interface RecordingResult {
  readonly blob: Blob;
  readonly mimeType: string;
  readonly durationMs: number;
  /** Measured client-side and sent to the scorer, which uses it to gate confidence. */
  readonly signal: { snrDb: number; speechRatio: number; clipped: boolean };
}

const SILENCE_HOLD_MS = 900;
const WATCHDOG_STALL_MS = 3_000;
const LEVEL_INTERVAL_MS = 50;

/**
 * Owns exactly one audio graph for its lifetime. Constructing a new recorder per session
 * (rather than reusing one) is deliberate: reuse is how competitors end up with audio
 * that works three times and then stops.
 */
export class Recorder {
  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];

  private levelTimer: ReturnType<typeof setInterval> | null = null;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;

  private startedAt = 0;
  private lastFrameAt = 0;
  private rebuilt = false;

  private speaking = false;
  private silenceSince: number | null = null;
  private speechFrames = 0;
  private totalFrames = 0;
  private noiseFloor = 0.004;
  private peak = 0;
  private clipped = false;

  constructor(private readonly events: RecorderEvents = {}) {}

  /** Claims the microphone and builds the graph. Throws a named error on failure. */
  async open(): Promise<void> {
    if (this.stream !== null) return;

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16_000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (error) {
      const name = (error as DOMException).name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        throw new RecorderError('permission_denied', MESSAGES.permission_denied);
      }
      if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        throw new RecorderError('no_device', MESSAGES.no_device);
      }
      if (name === 'NotReadableError' || name === 'AbortError') {
        throw new RecorderError('device_busy', MESSAGES.device_busy);
      }
      throw new RecorderError('unsupported', MESSAGES.unsupported);
    }

    // A track that ends underneath us (device unplugged, OS revoked) must surface as a
    // dead input rather than as silence.
    for (const track of this.stream.getAudioTracks()) {
      track.addEventListener('ended', () => this.events.onDead?.('track_ended'));
    }

    this.context = new AudioContext({ sampleRate: 16_000 });
    this.source = this.context.createMediaStreamSource(this.stream);
    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = 1024;
    this.analyser.smoothingTimeConstant = 0.6;
    this.source.connect(this.analyser);
  }

  /** Begins a take. Resets all per-utterance measurement. */
  start(): void {
    if (this.stream === null) throw new RecorderError('unsupported', 'Recorder is not open.');

    this.chunks = [];
    this.startedAt = Date.now();
    this.lastFrameAt = Date.now();
    this.speaking = false;
    this.silenceSince = null;
    this.speechFrames = 0;
    this.totalFrames = 0;
    this.peak = 0;
    this.clipped = false;

    const mimeType = pickMimeType();
    this.mediaRecorder = new MediaRecorder(this.stream, mimeType === null ? {} : { mimeType });
    this.mediaRecorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) {
        this.chunks.push(event.data);
        this.lastFrameAt = Date.now();
      }
    });
    this.mediaRecorder.start(100);

    this.levelTimer = setInterval(() => this.sampleLevel(), LEVEL_INTERVAL_MS);

    // The watchdog is the direct fix for "audio drops out after three lessons": if no
    // data arrives for 3 s we rebuild the graph once, then report the input as dead.
    this.watchdogTimer = setInterval(() => {
      if (Date.now() - this.lastFrameAt < WATCHDOG_STALL_MS) return;
      if (!this.rebuilt) {
        this.rebuilt = true;
        void this.rebuild();
        return;
      }
      this.events.onDead?.('input_stalled');
    }, 1_000);
  }

  /** Ends the take and returns the recording plus its measured signal quality. */
  async stop(): Promise<RecordingResult> {
    const recorder = this.mediaRecorder;
    if (recorder === null) throw new RecorderError('unsupported', 'Nothing is recording.');

    this.clearTimers();

    const finished = new Promise<void>((resolve) => {
      recorder.addEventListener('stop', () => resolve(), { once: true });
    });
    if (recorder.state !== 'inactive') recorder.stop();
    await finished;

    const mimeType = recorder.mimeType.length > 0 ? recorder.mimeType : 'audio/webm';
    const blob = new Blob(this.chunks, { type: mimeType });
    this.mediaRecorder = null;

    return {
      blob,
      mimeType,
      durationMs: Date.now() - this.startedAt,
      signal: this.signalQuality(),
    };
  }

  /** Releases the microphone. Always safe to call, and idempotent. */
  async close(): Promise<void> {
    this.clearTimers();
    try {
      if (this.mediaRecorder !== null && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
    } catch {
      // Already stopped.
    }
    this.mediaRecorder = null;

    this.source?.disconnect();
    this.analyser?.disconnect();
    this.source = null;
    this.analyser = null;

    if (this.context !== null) {
      try {
        await this.context.close();
      } catch {
        // Already closed.
      }
      this.context = null;
    }

    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
  }

  /** Rebuilds the graph once after a stall, preserving the in-progress take. */
  private async rebuild(): Promise<void> {
    const wasRecording = this.mediaRecorder !== null;
    await this.close();
    try {
      await this.open();
      if (wasRecording) this.start();
    } catch {
      this.events.onDead?.('input_stalled');
    }
  }

  private clearTimers(): void {
    if (this.levelTimer !== null) clearInterval(this.levelTimer);
    if (this.watchdogTimer !== null) clearInterval(this.watchdogTimer);
    this.levelTimer = null;
    this.watchdogTimer = null;
  }

  private sampleLevel(): void {
    const analyser = this.analyser;
    if (analyser === null) return;

    const buffer = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(buffer);

    let sumSquares = 0;
    let localPeak = 0;
    for (const sample of buffer) {
      sumSquares += sample * sample;
      const magnitude = Math.abs(sample);
      if (magnitude > localPeak) localPeak = magnitude;
    }

    const rms = Math.sqrt(sumSquares / buffer.length);
    this.totalFrames += 1;
    if (localPeak > this.peak) this.peak = localPeak;
    // 0.98 rather than 1.0: real clipping shows up just below full scale.
    if (localPeak >= 0.98) this.clipped = true;

    // Adaptive noise floor from the quiet frames, used for the SNR estimate.
    const isSpeech = rms > Math.max(0.01, this.noiseFloor * 3);
    if (!isSpeech) this.noiseFloor = this.noiseFloor * 0.95 + rms * 0.05;
    else this.speechFrames += 1;

    this.events.onLevel?.(Math.min(1, rms * 8));

    if (isSpeech) {
      this.silenceSince = null;
      if (!this.speaking) {
        this.speaking = true;
        this.events.onSpeechStart?.();
      }
      return;
    }

    if (!this.speaking) return;
    if (this.silenceSince === null) this.silenceSince = Date.now();
    else if (Date.now() - this.silenceSince >= SILENCE_HOLD_MS) {
      this.speaking = false;
      this.silenceSince = null;
      this.events.onSpeechEnd?.();
    }
  }

  /**
   * The signal measurements the server's confidence gate depends on. Reporting these
   * honestly is what allows the server to withhold a score instead of inventing one.
   */
  private signalQuality(): RecordingResult['signal'] {
    const floor = Math.max(1e-5, this.noiseFloor);
    const snrDb = this.peak <= 0 ? 0 : 20 * Math.log10(this.peak / floor);
    return {
      snrDb: Math.max(0, Math.min(60, Number(snrDb.toFixed(1)))),
      speechRatio: this.totalFrames === 0 ? 0 : this.speechFrames / this.totalFrames,
      clipped: this.clipped,
    };
  }
}

export class RecorderError extends Error {
  constructor(
    readonly problem: PreflightProblem,
    message: string,
  ) {
    super(message);
    this.name = 'RecorderError';
  }
}

/** Opus where available; the browser's default otherwise. */
export function pickMimeType(): string | null {
  const candidates = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm'];
  if (typeof MediaRecorder === 'undefined') return null;
  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return null;
}

/** Maps a recorder mime type onto the content types the upload endpoint accepts. */
export function uploadContentType(
  mimeType: string,
): 'audio/ogg;codecs=opus' | 'audio/webm;codecs=opus' | 'audio/wav' {
  if (mimeType.includes('ogg')) return 'audio/ogg;codecs=opus';
  if (mimeType.includes('wav')) return 'audio/wav';
  return 'audio/webm;codecs=opus';
}
